'use client';

import { ChartOptions } from 'chart.js';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox } from '../Checkbox/Checkbox';
import CircularText from '../CircularText';
import Counter from '../Counter/Counter';
import ElasticSlider from '../ElasticSlider/ElasticSlider';
import { Icon } from '../Icon/Icon';
import { ImageClient } from '../Image/ImageClient';
import ResponsiveChart from '../ResponsiveChart/ResponsiveChart';
import StatsCard from '../StatsCard';

import { InputFormClient } from '@/components/storyblok-components/Contact/components/Inputs/InputForm/InputFormClient';
import { InputPhoneClient } from '@/components/storyblok-components/Contact/components/Inputs/InputPhone/InputPhoneClient';
import { LocalizedPrice } from '@/components/storyblok-components/LocalizedPrice/LocalizedPrice';
import { useCurrency } from '@/providers/CurrencyProvider';
import { LocationStoryblok } from '@/types/storyblok/storyblok-types';
import { localizePriceCurrency } from '@/utils/storyblok';
import Stepper, { Step } from './Stepper';
import styles from './Stepper.module.scss';

export default function LocationStepperSection({ location }: { location: LocationStoryblok }) {
    const {
        content: { name, city, types, longTermRoi, shortTermRoi, appreciation },
    } = location;

    const t = useTranslations('StepperLocationSection');
    const g = useTranslations('Common');

    const [allowOffPlan, setAllowOffPlan] = useState(true);
    const [selectedTypes, setSelectedTypes] = useState<any[]>([]);
    const [counterValue, setCounterValue] = useState(5);

    const [selectedRentType, setSelectedRentType] = useState(
        (() => {
            if (longTermRoi) return 'longTerm';
            if (shortTermRoi) return 'shortTerm';
            return '';
        })(),
    );

    const locale = useLocale();
    const { currency, getExchangeRate } = useCurrency();
    const [currentStep, setCurrentStep] = useState(1);

    const [exchangeRate, setExchangeRate] = useState<number>(1);
    const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);

    const { control, watch, setValue, getValues, trigger } = useForm({
        defaultValues: {
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            company: '',
            investmentAmount: 0,
            investmentYears: 0,
        },
    });
    const triggerValidation = async () => {
        const isValid = await trigger(['firstname', 'lastname', 'email', 'phone']);

        if (isValid) {
            return true;
        }
    };

    const investmentAmount = watch('investmentAmount');
    const [amountDefaultCurrency, setAmountDefaultCurrency] = useState(null);

    const hasDiscountOffPlan = useMemo(() => {
        if (!types) return false;
        const discountPercents = types
            .map(type => parseFloat(type.discountOffPlanPercentage))
            .filter(discount => !Number.isNaN(discount) && discount > 0);

        return discountPercents.length > 0 ? Math.max(...discountPercents) : false;
    }, [types]);

    const { smallestStartingPrice, largestEndingPrice } = useMemo(() => {
        if (!types || types.length === 0) {
            return { smallestStartingPrice: null, largestEndingPrice: null };
        }

        const smallestStarting = types.reduce((min, curr) => {
            const price = parseFloat(curr.startingPrice);
            return price < min ? price : min;
        }, Infinity);

        const largestEnding = types.reduce((max, curr) => {
            const price = parseFloat(curr.endingPrice);
            return price > max ? price : max;
        }, -Infinity);

        return {
            smallestStartingPrice:
                hasDiscountOffPlan && allowOffPlan
                    ? smallestStarting * exchangeRate * (1 - hasDiscountOffPlan / 100)
                    : smallestStarting * exchangeRate,
            largestEndingPrice: largestEnding * exchangeRate,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exchangeRate, hasDiscountOffPlan, allowOffPlan]);

    const apartmentMatches = useMemo(() => {
        if (!types) return [];
        return types
            .filter(type => {
                const startingPrice = parseFloat(type.startingPrice);
                const endingPrice = parseFloat(type.endingPrice);
                return startingPrice <= amountDefaultCurrency && amountDefaultCurrency <= endingPrice;
            })
            .map(type => ({
                ...type,
                uniqueId: `${type.name}-base`,
                isOffplan: false,
            }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountDefaultCurrency]);

    const offPlanApartmentMatches = useMemo(() => {
        if (!types) return [];
        return types
            .filter(type => {
                const discountOffPlanPercentage = parseFloat(type.discountOffPlanPercentage);
                const discount = 1 - discountOffPlanPercentage / 100;
                const discountedStartingPrice = parseFloat(type.startingPrice) * discount;
                const discountedEndingPrice = parseFloat(type.endingPrice) * discount;

                // Compare the actual investment amount against the discounted prices.
                return (
                    discountedStartingPrice <= amountDefaultCurrency && amountDefaultCurrency <= discountedEndingPrice
                );
            })
            .map(type => ({
                ...type,
                uniqueId: `${type.name}-offplan`,
                isOffplan: true,
            }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountDefaultCurrency]);

    const toggleSelection = (selectedType: any) => {
        setSelectedTypes(prevSelectedTypes => {
            const selectedId = selectedType.uniqueId;
            if (prevSelectedTypes.some(v => v.uniqueId === selectedId)) {
                return prevSelectedTypes.filter(v => v.uniqueId !== selectedId);
            }
            return [...prevSelectedTypes, selectedType];
        });
    };

    useEffect(() => {
        async function fetchRate() {
            try {
                const rate = await getExchangeRate(city.content.currency, currency);
                setExchangeRate(rate);
            } catch (error) {
                console.error('Error fetching exchange rate', error);
            }
        }
        if (currency && city.content.currency) {
            fetchRate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    useEffect(() => {
        if (!amountDefaultCurrency) {
            setAmountDefaultCurrency(smallestStartingPrice + 10000);
        } else if (smallestStartingPrice !== null) {
            setValue('investmentAmount', amountDefaultCurrency * exchangeRate);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smallestStartingPrice]);

    const statsData = [
        { label: g('longTermRoi'), value: longTermRoi },
        { label: g('shortTermRoi'), value: shortTermRoi },
        { label: g('appreciation'), value: appreciation },
    ].filter(({ value }) => value && parseFloat(value) !== 0);

    const customOptions: ChartOptions<'bar'> = {
        plugins: {
            title: {
                text: 'Expected profits over years',
            },
        },
    };
    const appreciationRate = appreciation.includes('-')
        ? parseFloat(appreciation.split('-')[1])
        : parseFloat(appreciation);

    const dynamicChartData = useMemo(() => {
        const currentYear = new Date().getFullYear();
        let labels: string[] = [];
        labels = Array.from({ length: counterValue }, (_, i) => String(currentYear + i));

        const datasets = [
            {
                label: 'Investment',
                data: labels.map(() => investmentAmount),
                backgroundColor: 'rgba(173, 0, 38, 0.5)',
            },
            {
                label: 'Appreciation',
                data: labels.map((_, i) => {
                    if (i === 0) return 0;
                    return investmentAmount * ((1 + appreciationRate / 100) ** i - 1);
                }),
                backgroundColor: 'rgba(36, 97, 0, 0.5)',
            },
        ];

        if (selectedRentType) {
            const rentRate = selectedRentType === 'shortTerm' ? parseFloat(shortTermRoi) : parseFloat(longTermRoi);
            datasets.push({
                label: 'Rent',
                data: labels.map((_, i) => {
                    if (i === 0) return 0;
                    return investmentAmount * ((1 + rentRate / 100) ** i - 1);
                }),
                backgroundColor: 'rgba(255, 204, 0, 0.5)',
            });
        }

        return { labels, datasets };
    }, [counterValue, selectedRentType, appreciationRate, investmentAmount, longTermRoi, shortTermRoi]);

    const totalAppreciation = useMemo(
        () => (counterValue > 0 ? investmentAmount * ((1 + appreciationRate / 100) ** (counterValue - 1) - 1) : 0),
        [counterValue, investmentAmount, appreciationRate],
    );

    const totalRentingRoi = useMemo(() => {
        if (!selectedRentType) return 0;
        const rentRate = selectedRentType === 'shortTerm' ? parseFloat(shortTermRoi) : parseFloat(longTermRoi);
        return counterValue > 0 ? investmentAmount * ((1 + rentRate / 100) ** (counterValue - 1) - 1) : 0;
    }, [counterValue, investmentAmount, selectedRentType, shortTermRoi, longTermRoi]);

    const submitForm = async () => {
        const isValid = await triggerValidation();

        if (!isValid) return;

        const data = getValues();

        const { firstname, lastname, phone, company, email } = data;
        const payload = {
            firstname,
            surname: lastname,
            phone,
            company,
            email,
            investmentAmount,
            location: name,
            language: locale,
            currency,
            unitTypes:
                selectedTypes.length > 0 &&
                JSON.stringify(
                    selectedTypes.map(({ name: typeName, startingPrice, endingPrice, isOffplan }) => ({
                        name: typeName,
                        starting_price: startingPrice,
                        ending_price: endingPrice,
                        is_offplan: isOffplan,
                    })),
                ),
            marketingConsent: true,
        };

        if (!isSubmitSuccessful) {
            const response = await fetch('/api/locationForm/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (response.status !== 200) {
                setCurrentStep(3);
            } else {
                setIsSubmitSuccessful(true);
                setCurrentStep(4);
            }
        }
    };

    return (
        <Stepper
            currentStep={currentStep}
            onStepChange={step => {
                if (step === 4) {
                    submitForm();
                } else {
                    setCurrentStep(step);
                }
            }}
            disableStepIndicators
            backButtonText={currentStep !== 4 && t('common.previous')}
            nextButtonText={currentStep !== 4 && t('common.next')}
        >
            {/* INTRO */}
            <Step>
                <h2>{t('intro.title')}</h2>
                <p className={styles.stepOneDescription}>{t('intro.description')}</p>
            </Step>
            {/* INVESTMENT INFO */}
            <Step>
                <h2>{t('investment.title')}</h2>
                <p className={styles.stepOneDescription}>{t('investment.description')}</p>
                <div
                    style={{
                        marginTop: '12px ',
                    }}
                >
                    <Checkbox
                        label={t('checkbox.showOffplanDiscount')}
                        onChange={() => setAllowOffPlan(!allowOffPlan)}
                        disabled={!hasDiscountOffPlan}
                        checked={allowOffPlan}
                    />
                </div>
                <div className={styles.stepperSection}>
                    <Controller
                        name="investmentAmount"
                        control={control}
                        render={({ field }) => (
                            <ElasticSlider
                                isStepped
                                stepSize={1000}
                                startingValue={smallestStartingPrice}
                                defaultValue={field.value}
                                maxValue={largestEndingPrice}
                                onValueChange={e => {
                                    field.onChange(e);
                                    setAmountDefaultCurrency(e / exchangeRate);
                                }}
                            />
                        )}
                    />

                    <div className={styles.unitTypes}>
                        {apartmentMatches.map(type => {
                            const { name: typeName, image, startingPrice, endingPrice } = type;
                            return (
                                <div
                                    className={`${styles.unitTypesCard} ${selectedTypes.some(v => v.uniqueId === type.uniqueId) ? styles.selected : ''}`}
                                    onClick={() => toggleSelection(type)}
                                >
                                    <div className={styles.header}>
                                        <p className={styles.unitName}>{typeName}</p>
                                    </div>
                                    <div className={styles.imgWrap}>
                                        <ImageClient
                                            src={image?.filename}
                                            alt={image?.alt}
                                            width={100}
                                            height={100}
                                            focalPoints={image?.focus}
                                        />
                                    </div>
                                    <div className={styles.infowrap}>
                                        <div className={styles.row}>
                                            <div className={styles.priceTag}>{t('unitTypes.from')}</div>
                                            <div className={styles.priceTag}>
                                                <LocalizedPrice
                                                    price={startingPrice}
                                                    defaultCurrency={city.content.currency}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.row}>
                                            <div className={styles.priceTag}>{t('unitTypes.to')}</div>
                                            <div className={styles.priceTag}>
                                                <LocalizedPrice
                                                    price={endingPrice}
                                                    defaultCurrency={city.content.currency}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {allowOffPlan &&
                            offPlanApartmentMatches.map(type => {
                                const {
                                    name: typeName,
                                    image,
                                    startingPrice,
                                    endingPrice,
                                    discountOffPlanPercentage,
                                } = type;
                                return (
                                    <div
                                        className={`${styles.unitTypesCard} ${
                                            selectedTypes.some(v => v.uniqueId === type.uniqueId) ? styles.selected : ''
                                        }`}
                                        onClick={() => toggleSelection(type)}
                                    >
                                        <div className={styles.header}>
                                            <p className={styles.unitName}>{typeName}</p>
                                        </div>
                                        <div className={styles.imgWrap}>
                                            <ImageClient
                                                src={image?.filename}
                                                alt={image?.alt}
                                                width={100}
                                                height={100}
                                                focalPoints={image?.focus}
                                            />
                                            <div className={styles.circularOffplan}>
                                                <CircularText
                                                    text="OFF*PLAN*DISCOUNT*"
                                                    onHover="speedUp"
                                                    spinDuration={20}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.infowrap}>
                                            <div className={styles.row}>
                                                <div className={styles.priceTag}>{t('unitTypes.from')}</div>
                                                <div className={styles.priceTag}>
                                                    <LocalizedPrice
                                                        price={startingPrice * (1 - discountOffPlanPercentage / 100)}
                                                        defaultCurrency={city.content.currency}
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.row}>
                                                <div className={styles.priceTag}>to:</div>
                                                <div className={styles.priceTag}>
                                                    <LocalizedPrice
                                                        price={endingPrice * (1 - discountOffPlanPercentage / 100)}
                                                        defaultCurrency={city.content.currency}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                    {selectedTypes.length > 0 && (
                        <div style={{ marginTop: '16px', fontWeight: 'bold' }}>
                            {t('unitTypes.selected')}{' '}
                            {selectedTypes
                                .map(type => (type.isOffplan ? `${type.name} ${t('offplan')}` : type.name))
                                .join(', ')}
                        </div>
                    )}
                </div>
            </Step>

            {/* CONTACT FORM */}
            <Step>
                <h2>{t('form.title')}</h2>
                <p
                    style={{
                        marginTop: '36px',
                    }}
                >
                    {t('form.description')}
                </p>

                <div className={styles.form}>
                    <Controller
                        control={control}
                        rules={{
                            required: t('form.errors.firstnameRequired'),
                            pattern: {
                                value: /^[A-Za-z\s]+$/,
                                message: t('form.errors.invalidFirstname'),
                            },
                        }}
                        name="firstname"
                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                            <>
                                <InputFormClient
                                    className={styles.input}
                                    type="text"
                                    blok={{
                                        label: t('form.labels.firstname'),
                                        width: '50%',
                                        placeholder: t('form.placeholders.firstname'),
                                        component: 'inputForm',
                                        _uid: 'any',
                                    }}
                                    props={{ onChange, onBlur, value, ref }}
                                    required
                                    error={error}
                                    isTouched={!!error}
                                />
                                {/* {error && <p className={styles.errorMessage}>{error.message}</p>} */}
                            </>
                        )}
                    />
                    <Controller
                        control={control}
                        rules={{
                            required: t('form.errors.lastnameRequired'),
                            pattern: {
                                value: /^[A-Za-z\s]+$/,
                                message: t('form.errors.invalidLastname'),
                            },
                        }}
                        name="lastname"
                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                            <InputFormClient
                                className={styles.input}
                                type="text"
                                blok={{
                                    label: t('form.labels.lastname'),
                                    width: '50%',
                                    placeholder: t('form.placeholders.lastname'),
                                    component: 'inputForm',
                                    _uid: 'any',
                                }}
                                props={{ onChange, onBlur, value, ref }}
                                required
                                error={error}
                                isTouched={!!error}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        rules={{
                            required: t('form.errors.emailRequired'),
                            pattern: {
                                value: /^[\w.-]+@[a-zA-Z0-9,-]+\.[a-zA-Z]{2,4}$/,
                                message: t('form.errors.invalidEmail'),
                            },
                        }}
                        name="email"
                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                            <InputFormClient
                                className={styles.input}
                                type="email"
                                blok={{
                                    label: t('form.labels.email'),
                                    width: '50%',
                                    placeholder: t('form.placeholders.email'),
                                    component: 'inputForm',
                                    _uid: 'any',
                                }}
                                props={{ onChange, onBlur, value, ref }}
                                required
                                error={error}
                                isTouched={!!error}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        rules={{
                            required: t('form.errors.phoneRequired'),
                            pattern: {
                                value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{3,8}$/,
                                message: t('form.errors.invalidPhone'),
                            },
                        }}
                        name="phone"
                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                            <InputPhoneClient
                                type="tel"
                                className={styles.input}
                                blok={{
                                    label: t('form.labels.phone'),
                                    width: '50%',
                                    placeholder: t('form.placeholders.phone'),
                                    component: 'inputForm',
                                    _uid: 'any',
                                }}
                                required
                                props={{ onChange, onBlur, value, ref }}
                                error={error}
                                isTouched={!!error}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="company"
                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                            <InputFormClient
                                className={styles.input}
                                type="text"
                                blok={{
                                    label: t('form.labels.company'),
                                    width: '50%',
                                    placeholder: t('form.placeholders.company'),
                                    component: 'inputForm',
                                    _uid: 'any',
                                }}
                                props={{ onChange, onBlur, value, ref }}
                                error={error}
                                isTouched={isTouched}
                            />
                        )}
                    />
                </div>
            </Step>

            {/* RESULTS */}
            <Step>
                <h2>How Much Would You earn over the years?</h2>
                <p className={styles.stepOneDescription}>
                    Based on the amount you entered, we’ve prepared a simulation that shows the potential earnings over
                    time in this area. This overview is designed to give you a clear picture of the possible returns on
                    your investment.
                </p>
                <div
                    style={{
                        marginTop: '22px',
                    }}
                >
                    <h4>
                        The amount you want to invest:{' '}
                        <span
                            style={{
                                fontWeight: '900',
                            }}
                        >
                            {localizePriceCurrency(investmentAmount, currency, currency, locale).default}
                        </span>
                    </h4>
                </div>

                <div className={styles.middleWrap}>
                    {statsData.length > 0 && (
                        <div
                            style={{
                                marginTop: '16px',
                            }}
                        >
                            <h4>Expected return in this location:</h4>
                            <div
                                style={{
                                    maxWidth: 'fit-content',
                                    marginTop: '8px',
                                }}
                            >
                                <StatsCard stats={statsData} />
                            </div>
                        </div>
                    )}

                    <div className={styles.counterSection}>
                        <h4>How many years you want simulate your investment?</h4>
                        <div className={styles.counterWrap}>
                            <h3>Years:</h3>
                            <div className={styles.wrp}>
                                <Counter
                                    value={counterValue}
                                    places={[100, 10, 1]}
                                    fontSize={80}
                                    padding={5}
                                    gap={10}
                                    textColor="white"
                                    fontWeight={900}
                                />
                                <div
                                    className={styles.counterAction}
                                    style={{ marginTop: '10px' }}
                                >
                                    <Icon
                                        className={clsx(styles.counterActionIcon, {
                                            [styles.disabled]: counterValue <= 5,
                                        })}
                                        onClick={() =>
                                            counterValue <= 5 ? undefined : setCounterValue(prev => prev - 1)
                                        }
                                        style={{ marginRight: '5px', fontSize: '22px' }}
                                        name="minus"
                                    />
                                    <Icon
                                        className={clsx(styles.counterActionIcon, {
                                            [styles.disabled]: counterValue >= 50,
                                        })}
                                        onClick={() =>
                                            counterValue >= 50 ? undefined : setCounterValue(prev => prev + 1)
                                        }
                                        style={{ marginRight: '5px', fontSize: '22px' }}
                                        name="plus"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.simulationSection}>
                    {(longTermRoi || shortTermRoi) && (
                        <div>
                            <h4>Do you plan to rent?</h4>
                            {longTermRoi && (
                                <div
                                    style={{
                                        marginTop: '12px ',
                                    }}
                                >
                                    <Checkbox
                                        label="Long term rent"
                                        onChange={() =>
                                            setSelectedRentType(selectedRentType === 'longTerm' ? null : 'longTerm')
                                        }
                                        checked={selectedRentType === 'longTerm'}
                                    />
                                </div>
                            )}

                            {shortTermRoi && (
                                <div
                                    style={{
                                        marginTop: '12px ',
                                    }}
                                >
                                    <Checkbox
                                        label="Short term rent"
                                        onChange={() =>
                                            setSelectedRentType(selectedRentType === 'shortTerm' ? null : 'shortTerm')
                                        }
                                        checked={selectedRentType === 'shortTerm'}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles.expectedProfits}>
                        <h4>Your expected profits:</h4>
                        <div
                            style={{
                                minHeight: '300px',
                            }}
                        >
                            <ResponsiveChart
                                data={dynamicChartData}
                                options={customOptions}
                            />
                        </div>

                        <h4
                            style={{
                                marginTop: '36px',
                            }}
                        >
                            Overall, after {counterValue} years, you gonna gain:
                        </h4>
                        <div>
                            <div
                                style={{
                                    marginTop: '12px',
                                }}
                            >
                                <LocalizedPrice
                                    className={styles.localized}
                                    price={totalAppreciation}
                                />{' '}
                                is the total increased value of your property
                            </div>
                            <div
                                style={{
                                    marginTop: '12px',
                                }}
                            >
                                <LocalizedPrice
                                    className={styles.localized}
                                    price={totalRentingRoi}
                                />{' '}
                                is what you gain renting out your property
                            </div>
                        </div>
                    </div>
                </div>
            </Step>
        </Stepper>
    );
}
